import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../components/DashboardLayout';
import { GLIMState } from '@/brand';
import SoMSubNav from '../components/SoMSubNav';
import { Button } from '../components/ui/button';
import {
  Trophy, Globe, Eye, ChatCircle, ShoppingCart, ArrowUp, ArrowDown,
  Lightning, Warning, CircleNotch, CheckCircle
} from '@phosphor-icons/react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, LineChart, Line
} from 'recharts';
import { toast } from 'sonner';
import axios from 'axios';
import { clientsAPI } from '../lib/api';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { formatProductionPath, formatSomHistoryDate } from '../lib/somHistoryHelpers';
import { somProductionKpiView } from '../lib/somDashboardHelpers';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('nx_token')}` } });
const PIE_COLORS = ['var(--accent)', 'var(--success)', 'var(--warning)', 'var(--error)'];

const KpiDot = ({ status }) => {
  const c = { red: 'var(--error)', amber: 'var(--warning)', green: 'var(--success)' };
  return <span className="w-2 h-2 rounded-full inline-block" style={{ background: c[status] || 'var(--text-tertiary)' }} />;
};
const NotAvailable = () => <p className="text-sm text-[var(--text-secondary)] mt-2">Not available</p>;

export default function SoMPage() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [triggering, setTriggering] = useState('');
  // P2.3 -- Share of Model is a per-brand measurement. This page had no
  // client picker, so /som/dashboard returned the newest som_scores row
  // across the whole account -- for a multi-brand workspace that is
  // whichever brand was checked last, not the one the founder is looking
  // at. Same local clientsAPI.list() + <Select> pattern the sibling SoM
  // pages (History/Prompts/Usage) already use.
  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = useState('');

  useEffect(() => {
    clientsAPI.list()
      .then((r) => {
        setClients(r.data || []);
        if (r.data?.length) setClientId(r.data[0].id);
        else setLoading(false); // no brands -> nothing to fetch, stop the spinner
      })
      .catch(() => { toast.error('Failed to load clients'); setLoading(false); });
  }, []);

  useEffect(() => {
    if (!clientId) return;
    setLoading(true);
    setData(null); // never show the previous brand's score while switching
    axios.get(`${API}/som/dashboard?client_id=${encodeURIComponent(clientId)}`, auth())
      .then(r => setData(r.data))
      .catch(() => toast.error('Failed to load SoM dashboard'))
      .finally(() => setLoading(false));
  }, [clientId]);

  const triggerAgent = (agent) => {
    setTriggering(agent);
    toast.success(`${agent} agent triggered`);
    const map = { geo: '/seo', entity: '/entity', creative: '/creative-lab', messaging: '/vault' };
    setTimeout(() => { setTriggering(''); if (map[agent]) navigate(map[agent]); }, 1200);
  };

  if (loading) return <DashboardLayout><div className="flex items-center justify-center h-64"><GLIMState state="analyzing" size={72} label="Analyzing" /></div></DashboardLayout>;

  const kpi = data?.kpi_cards || {};
  const lm = data?.live_metrics || {};
  const production = data?.production || {};
  const somView = somProductionKpiView(production, kpi.som);
  const hasProduction = somView.hasData;
  const somLive = somView.isLive;
  const somValue = somView.score;

  // Donut data — surface_distribution is unavailable unless has_data is true
  const surfaceDist = data?.surface_distribution;
  const surfaceData = (surfaceDist?.has_data && Array.isArray(surfaceDist.data))
    ? surfaceDist.data.map((s) => ({
        name: (s.name || '').replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()), value: s.value,
      }))
    : [];
  const surfaceRoutes = { 'Informational Ai': '/seo', 'Visual Social': '/creative-lab', 'Trust Community': '/search', 'Transactional Shopping': '/marketplace' };

  // Brand vs Competitors — real visibility_rate from ai_responses, not fabricated per-LLM scores
  const compComparison = data?.competitor_comparison || {};
  const competitorChartData = compComparison.is_live
    ? [
        // P0: the brand's rate is the check-level SoM and may be absent (no
        // scored check yet) -- it is omitted then, never plotted as 0.
        ...(typeof compComparison.brand?.visibility_rate === 'number'
          ? [{ name: 'Your Brand', visibility: compComparison.brand.visibility_rate }]
          : []),
        ...(compComparison.competitors || []).map(c => ({ name: c.name, visibility: c.visibility_rate })),
      ]
    : [];

  return (
    <DashboardLayout>
      <div className="space-y-5" data-testid="som-page">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div>
            <h1 className="font-display text-2xl sm:text-3xl font-black text-[var(--text-primary)] tracking-tight">Share of Model</h1>
            <p className="text-sm text-[var(--text-secondary)] mt-1" data-testid="som-definition">
              The share of usable AI answers in your latest check that mention your brand. It comes from the same check as the
              Briefing's “X of N AI answers mention you”; answers that failed are left out rather than counted as misses.
            </p>
          </div>
          <Select value={clientId} onValueChange={setClientId}>
            <SelectTrigger className="w-full sm:w-48 bg-[var(--surface-sunken)] border-[var(--border)] text-[var(--text-primary)]" data-testid="som-page-client-select">
              <SelectValue placeholder="Select client" />
            </SelectTrigger>
            <SelectContent className="bg-[var(--surface)] border-[var(--border)]">
              {clients.map((c) => (
                <SelectItem key={c.id} value={c.id} className="text-[var(--text-primary)] hover:bg-[var(--neutral-bg)]">{c.display_name || c.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <SoMSubNav />

        {/* ═══ TOP 5 KPI CARDS ═══ */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="som-kpis">
          <div className="nx-metric-card rounded-lg" data-testid="som-kpi-production-score">
            <div className="flex items-center gap-1.5 mb-2">
              <KpiDot status={somLive ? kpi.som?.status : null} />
              <span className="text-[9px] text-[var(--text-tertiary)] font-mono uppercase tracking-widest">Share of Model · latest check</span>
              {somLive
                ? <span className="px-1.5 py-0.5 text-[9px] font-mono bg-[var(--success-bg)] text-[var(--success)] rounded">Live</span>
                : <span className="px-1.5 py-0.5 text-[9px] font-mono bg-[var(--neutral-bg)] text-[var(--text-secondary)] rounded">No data</span>}
            </div>
            {somLive ? (
              <>
                <p className="text-3xl font-black font-mono" style={{ color: kpi.som?.status === 'green' ? 'var(--success)' : kpi.som?.status === 'amber' ? 'var(--warning)' : 'var(--error)' }}>
                  {somValue}%
                </p>
                <span className={`flex items-center gap-0.5 text-xs font-mono mt-1 ${production.score_change == null ? 'text-[var(--text-secondary)]' : production.score_change >= 0 ? 'text-[var(--success)]' : 'text-[var(--error)]'}`}>
                  {production.score_change == null ? null : production.score_change >= 0 ? <ArrowUp size={10} /> : <ArrowDown size={10} />}
                  {production.score_change == null ? '—' : `${production.score_change > 0 ? '+' : ''}${production.score_change}`} vs previous
                </span>
                {somView.changeNote && (
                  <p className="text-[9px] text-[var(--text-tertiary)] mt-1" data-testid="som-production-change-note">{somView.changeNote}</p>
                )}
              </>
            ) : (
              <p className="text-sm text-[var(--text-secondary)] mt-2" data-testid="som-production-empty">{somView.emptyMessage}</p>
            )}
          </div>
          <div className="nx-metric-card rounded-lg">
            <div className="flex items-center gap-1.5 mb-2"><Globe size={12} className="text-[var(--success)]" weight="fill" /><span className="text-[9px] text-[var(--text-tertiary)] font-mono uppercase tracking-widest">Discovery Reach</span></div>
            {kpi.discovery_reach?.is_live ? (
              <>
                <p className="text-3xl font-black text-[var(--success)] font-mono">{kpi.discovery_reach.value.toLocaleString('en-IN')}</p>
                <span className="text-xs font-mono text-[var(--success)] mt-1"><ArrowUp size={10} className="inline" /> +{kpi.discovery_reach.delta_30d}% 30d</span>
              </>
            ) : <NotAvailable />}
          </div>
          <div className="nx-metric-card rounded-lg">
            <div className="flex items-center gap-1.5 mb-2"><KpiDot status={kpi.aigvr?.is_live ? kpi.aigvr?.status : null} /><span className="text-[9px] text-[var(--text-tertiary)] font-mono uppercase tracking-widest">AI Visibility</span></div>
            {kpi.aigvr?.is_live ? (
              <>
                <p className="text-3xl font-black font-mono" style={{ color: kpi.aigvr.status === 'green' ? 'var(--success)' : kpi.aigvr.status === 'amber' ? 'var(--warning)' : 'var(--error)' }}>{kpi.aigvr.value}%</p>
                <span className="text-[9px] text-[var(--text-tertiary)] font-mono mt-1">{kpi.aigvr.must_win} must-win prompts</span>
              </>
            ) : <NotAvailable />}
          </div>
          <div className="nx-metric-card rounded-lg">
            <div className="flex items-center gap-1.5 mb-2"><ChatCircle size={12} className="text-[var(--accent)]" weight="fill" /><span className="text-[9px] text-[var(--text-tertiary)] font-mono uppercase tracking-widest">Vernacular Index</span></div>
            {kpi.vernacular?.is_live ? (
              <>
                <p className="text-3xl font-black text-[var(--accent)] font-mono">{kpi.vernacular.value}%</p>
                <p className="text-[8px] text-[var(--success)] font-mono mt-1">{kpi.vernacular.growth_label}</p>
              </>
            ) : <NotAvailable />}
          </div>
          <div className="nx-metric-card rounded-lg">
            <div className="flex items-center gap-1.5 mb-2"><ShoppingCart size={12} className="text-[var(--success)]" weight="fill" /><span className="text-[9px] text-[var(--text-tertiary)] font-mono uppercase tracking-widest">Non-Google Conv.</span></div>
            {kpi.non_google?.is_live ? (
              <>
                <p className="text-3xl font-black text-[var(--success)] font-mono">{kpi.non_google.value}</p>
                <p className="text-[9px] text-[var(--text-tertiary)] font-mono mt-1">${(kpi.non_google.revenue || 0).toLocaleString()} revenue</p>
              </>
            ) : <NotAvailable />}
          </div>
        </div>

        <div className="nx-metric-card rounded-lg" data-testid="som-production-summary">
          <h3 className="font-display text-sm font-bold text-[var(--text-primary)] mb-3">Production check summary</h3>
          {hasProduction ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
              <div>
                <p className="text-[9px] text-[var(--text-tertiary)] font-mono uppercase">Latest score</p>
                <p className="text-[var(--text-primary)] font-mono" data-testid="som-production-latest-score">{somView.latestScoreLabel}</p>
              </div>
              <div>
                <p className="text-[9px] text-[var(--text-tertiary)] font-mono uppercase">Previous score</p>
                <p className="text-[var(--text-primary)] font-mono">{production.previous_score == null ? 'Unavailable' : `${production.previous_score}%`}</p>
              </div>
              <div>
                <p className="text-[9px] text-[var(--text-tertiary)] font-mono uppercase">Checks</p>
                <p className="text-[var(--text-primary)] font-mono">{production.check_count}</p>
              </div>
              <div>
                <p className="text-[9px] text-[var(--text-tertiary)] font-mono uppercase">Path</p>
                <p className="text-[var(--text-primary)] font-mono" data-testid="som-production-path">{production.latest_execution_path_label || formatProductionPath({ execution_path: production.latest_execution_path })}</p>
              </div>
              <div>
                <p className="text-[9px] text-[var(--text-tertiary)] font-mono uppercase">Last checked</p>
                <p className="text-[var(--text-primary)] font-mono">{formatSomHistoryDate(production.last_checked_at)}</p>
              </div>
              <div>
                <p className="text-[9px] text-[var(--text-tertiary)] font-mono uppercase">Prompt coverage</p>
                <p className="text-[var(--text-primary)] font-mono" data-testid="som-production-coverage">{production.prompt_coverage || 'Unavailable'}</p>
              </div>
              <div>
                <p className="text-[9px] text-[var(--text-tertiary)] font-mono uppercase">Mentions</p>
                <p className="text-[var(--text-primary)] font-mono">{production.mentions_coverage || 'Unavailable'}</p>
              </div>
              <div>
                <p className="text-[9px] text-[var(--text-tertiary)] font-mono uppercase">Failed prompts</p>
                <p className="text-[var(--text-primary)] font-mono">{production.failed_prompts == null ? 'Unavailable' : production.failed_prompts}</p>
              </div>
              <div>
                <p className="text-[9px] text-[var(--text-tertiary)] font-mono uppercase">Measurement method</p>
                <p className="text-[var(--text-primary)] font-mono" data-testid="som-production-methodology">{somView.methodologyLabel}</p>
              </div>
            </div>
          ) : (
            <p className="text-sm text-[var(--text-secondary)]">No production SoM checks yet. History will appear after a 4-prompt production run.</p>
          )}
        </div>

        {hasProduction && (
          <div className="nx-metric-card rounded-lg" data-testid="som-score-trend">
            <h3 className="font-display text-sm font-bold text-[var(--text-primary)] mb-3">Score over time</h3>
            <div className="h-48">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={(production.score_trend || []).map((p) => ({
                  ...p,
                  label: formatSomHistoryDate(p.created_at),
                }))}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                  <XAxis dataKey="label" tick={{ fill: 'var(--text-secondary)', fontSize: 10 }} axisLine={false} />
                  <YAxis tick={{ fill: 'var(--text-tertiary)', fontSize: 10 }} axisLine={false} domain={[0, 100]} />
                  <Tooltip contentStyle={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8, color: 'var(--text-primary)', fontSize: 11 }} />
                  <Line type="monotone" dataKey="score" stroke="var(--accent)" strokeWidth={2} dot={{ r: 3 }} name="SoM score" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* ═══ DONUT + COMPETITOR COMPARISON ═══ */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Donut */}
          <div className="nx-metric-card rounded-lg" data-testid="surface-donut">
            <h3 className="font-display text-sm font-bold text-[var(--text-primary)] mb-3">Search Surface Distribution</h3>
            {surfaceData.length > 0 ? (
              <>
                <div className="h-52">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={surfaceData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={75} innerRadius={42}
                        onClick={(_, i) => { const route = Object.values(surfaceRoutes)[i]; if (route) navigate(route); }}>
                        {surfaceData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} style={{ cursor: 'pointer' }} />)}
                      </Pie>
                      <Tooltip contentStyle={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8, color: 'var(--text-primary)', fontSize: 11 }} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="grid grid-cols-2 gap-1 mt-1">
                  {surfaceData.map((s, i) => (
                    <button key={s.name} onClick={() => { const route = Object.values(surfaceRoutes)[i]; if (route) navigate(route); }}
                      className="flex items-center gap-1.5 text-[9px] text-[var(--text-secondary)] hover:text-[var(--text-primary)] p-1 rounded hover:bg-[var(--neutral-bg)] transition-colors duration-200">
                      <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: PIE_COLORS[i] }} />
                      <span className="truncate">{s.name}</span>
                      <span className="font-mono ml-auto">{s.value}</span>
                    </button>
                  ))}
                </div>
              </>
            ) : (
              <p className="text-sm text-[var(--text-secondary)] h-52 flex items-center justify-center text-center">No surface data available</p>
            )}
          </div>

          {/* Brand vs Competitors */}
          <div className="lg:col-span-2 nx-metric-card rounded-lg" data-testid="llm-comparison">
            <h3 className="font-display text-sm font-bold text-[var(--text-primary)] mb-3">
              <Trophy size={16} className="inline mr-2 text-[var(--accent)]" weight="fill" />Brand vs Competitors
            </h3>
            {competitorChartData.length > 0 ? (
              <>
                <div className="h-56">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={competitorChartData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                      <XAxis dataKey="name" tick={{ fill: 'var(--text-secondary)', fontSize: 10 }} axisLine={false} />
                      <YAxis tick={{ fill: 'var(--text-tertiary)', fontSize: 10 }} axisLine={false} domain={[0, 100]} />
                      <Tooltip contentStyle={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8, color: 'var(--text-primary)', fontSize: 11 }} />
                      <Bar dataKey="visibility" name="Visibility rate" fill="var(--accent)" radius={[3, 3, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <p className="text-[8px] text-[var(--text-tertiary)] font-mono mt-1">Visibility rate across recorded AI responses</p>
              </>
            ) : (
              <p className="text-sm text-[var(--text-secondary)] h-56 flex items-center justify-center text-center">No competitor visibility data available</p>
            )}
          </div>
        </div>

        {/* ═══ PROACTIVE ORCHESTRATION TABLE ═══ */}
        {data?.intelligence?.length > 0 && (
          <div className="nx-metric-card rounded-lg" data-testid="som-intelligence">
            <h3 className="font-display text-sm font-bold text-[var(--text-primary)] mb-4">
              <Lightning size={16} className="inline mr-2 text-[var(--warning)]" weight="fill" />Proactive Orchestration
            </h3>
            <table className="w-full border-collapse">
              <thead><tr className="border-b border-[var(--border)]">
                <th className="text-left text-[9px] text-[var(--text-tertiary)] font-mono uppercase p-2 w-[25%]">Signal</th>
                <th className="text-left text-[9px] text-[var(--text-tertiary)] font-mono uppercase p-2 w-[40%]">Intelligence</th>
                <th className="text-left text-[9px] text-[var(--text-tertiary)] font-mono uppercase p-2 w-[35%]">Action</th>
              </tr></thead>
              <tbody>
                {data.intelligence.map(row => (
                  <tr key={row.id} className="border-b border-[var(--border-light)]">
                    <td className="p-2"><div className="flex items-center gap-2"><span className="w-2 h-2 rounded-full" style={{ background: row.severity === 'high' ? 'var(--error)' : 'var(--warning)' }} /><span className="text-xs text-[var(--text-primary)]">{row.indicator}</span></div></td>
                    <td className="p-2 text-xs text-[var(--text-secondary)]">{row.intelligence}</td>
                    <td className="p-2"><p className="text-[10px] text-[var(--text-secondary)] mb-1.5">{row.action}</p>
                      <Button size="sm" onClick={() => triggerAgent(row.agent)} disabled={triggering === row.agent}
                        className="bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-white text-[10px] h-7 px-3">
                        {triggering === row.agent ? <CircleNotch size={10} className="animate-spin mr-1" /> : <Lightning size={10} className="mr-1" />}Trigger Agent
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* ═══ 4 LIVE METRICS ═══ */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="som-live-metrics">
          {/* M1: Brand Sentiment */}
          {lm.sentiment && (
            <div className="nx-metric-card rounded-lg">
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs font-bold text-[var(--text-primary)]">Brand Sentiment (AI Decoded)</span>
                {lm.sentiment.auto_trigger && <span className="text-[9px] bg-[var(--error-bg)] text-[var(--error)] px-2 py-0.5 rounded font-mono">Identity Correction Queued</span>}
              </div>
              <p className="text-3xl font-black font-mono mb-2" style={{ color: lm.sentiment.overall_score >= 75 ? 'var(--success)' : lm.sentiment.overall_score >= 50 ? 'var(--warning)' : 'var(--error)' }}>{lm.sentiment.overall_score}/100</p>
              <div className="space-y-1 mb-2">
                {Object.entries(lm.sentiment.per_llm || {}).map(([llm_name, d]) => (
                  <div key={llm_name} className="flex items-center gap-2 text-[10px] font-mono">
                    <span className="w-20 text-[var(--text-secondary)]">{llm_name}</span>
                    <div className="flex-1 h-1.5 bg-[var(--neutral-bg)] rounded-full overflow-hidden"><div className="h-full bg-[var(--success)] rounded-full" style={{ width: `${d.positive}%` }} /></div>
                    <span className="text-[var(--text-tertiary)] w-16">{d.accuracy}</span>
                  </div>
                ))}
              </div>
              <div className="flex flex-wrap gap-1">
                {lm.sentiment.top_positive?.map(p => <span key={p} className="px-1.5 py-0.5 text-[8px] bg-[var(--success-bg)] text-[var(--success)] rounded font-mono">{p}</span>)}
                {lm.sentiment.top_concerning?.map(p => <span key={p} className="px-1.5 py-0.5 text-[8px] bg-[var(--error-bg)] text-[var(--error)] rounded font-mono">{p}</span>)}
              </div>
            </div>
          )}

          {/* M2: Crawl-to-Referral */}
          {lm.crawl_table && (
            <div className="nx-metric-card rounded-lg">
              <span className="text-xs font-bold text-[var(--text-primary)] mb-3 block">Crawl-to-Referral Ratio</span>
              <table className="w-full text-[10px] font-mono">
                <thead><tr className="border-b border-[var(--border)] text-[var(--text-tertiary)]">
                  <th className="text-left p-1">Bot</th><th className="text-right p-1">Crawled</th><th className="text-right p-1">Refs</th><th className="text-right p-1">Ratio</th><th className="text-right p-1">Trend</th><th className="text-left p-1">Action</th>
                </tr></thead>
                <tbody>
                  {lm.crawl_table.map(r => (
                    <tr key={r.bot} className={`border-b border-[var(--border-light)] ${r.alert ? 'bg-[var(--error-bg)]' : ''}`}>
                      <td className="p-1 text-[var(--text-secondary)]">{r.bot}</td>
                      <td className="p-1 text-right text-[var(--text-primary)]">{r.pages_crawled_30d.toLocaleString()}</td>
                      <td className={`p-1 text-right ${r.referrals_sent < 10 ? 'text-[var(--error)]' : 'text-[var(--success)]'}`}>{r.referrals_sent}</td>
                      <td className="p-1 text-right text-[var(--text-primary)]">{r.ratio_pct}%</td>
                      <td className={`p-1 text-right ${r.trend_30d >= 0 ? 'text-[var(--success)]' : 'text-[var(--error)]'}`}>{r.trend_30d > 0 ? '+' : ''}{r.trend_30d}%</td>
                      <td className="p-1">{r.alert ? <Warning size={10} className="text-[var(--error)] inline" weight="fill" /> : <CheckCircle size={10} className="text-[var(--success)] inline" weight="fill" />} <span className="text-[var(--text-tertiary)]">{r.action}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* M3: CER */}
          {lm.cer && (
            <div className="nx-metric-card rounded-lg">
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs font-bold text-[var(--text-primary)]">Content Extraction Rate</span>
                <span className="text-[9px] text-[var(--text-tertiary)] font-mono">Industry avg: 22% | Target: 40%+</span>
              </div>
              <div className="space-y-2">
                {lm.cer.types?.map((t, i) => (
                  <div key={t.type} className="flex items-center gap-2">
                    <span className="text-[10px] text-[var(--text-secondary)] w-28">{t.type}</span>
                    <div className="flex-1 h-2.5 bg-[var(--neutral-bg)] rounded-full overflow-hidden relative">
                      <div className="absolute h-full border-r border-dashed border-[var(--warning-border)]" style={{ left: '40%' }} title="Target: 40%" />
                      <div className="h-full rounded-full" style={{ width: `${t.cer}%`, background: t.cer >= 40 ? 'var(--success)' : t.cer >= 22 ? 'var(--warning)' : 'var(--error)' }} />
                    </div>
                    <span className="text-[10px] font-mono text-[var(--text-primary)] w-10 text-right">{t.cer}%</span>
                    {i === lm.cer.types.length - 1 && <span className="text-[8px] bg-[var(--error-bg)] text-[var(--error)] px-1 rounded font-mono">Auto-Queued</span>}
                  </div>
                ))}
              </div>
              <p className="text-[8px] text-[var(--text-tertiary)] font-mono mt-2 italic">Lowest: "{lm.cer.lowest_auto_queued}" auto-queued for SEO Agent restructuring</p>
            </div>
          )}

          {/* M4: Buy-Box */}
          {lm.buybox && (
            <div className="nx-metric-card rounded-lg">
              <span className="text-xs font-bold text-[var(--text-primary)] mb-3 block">Buy-Box Ownership Rate</span>
              <table className="w-full text-[10px] font-mono">
                <thead><tr className="border-b border-[var(--border)] text-[var(--text-tertiary)]">
                  <th className="text-left p-1">Marketplace</th><th className="text-right p-1">Brand %</th><th className="text-right p-1">3P Seller %</th><th className="text-right p-1">Price Diff</th><th className="text-left p-1">Stock</th><th className="text-left p-1">Action</th>
                </tr></thead>
                <tbody>
                  {lm.buybox.map(b => (
                    <tr key={b.marketplace} className={`border-b border-[var(--border-light)] ${b.alert ? 'bg-[var(--error-bg)]' : ''}`}>
                      <td className="p-1 text-[var(--text-secondary)]">{b.marketplace}</td>
                      <td className={`p-1 text-right font-bold ${b.brand_buybox_pct >= 70 ? 'text-[var(--success)]' : 'text-[var(--error)]'}`}>{b.brand_buybox_pct}%</td>
                      <td className={`p-1 text-right ${b.third_party_pct > 30 ? 'text-[var(--error)]' : 'text-[var(--text-primary)]'}`}>{b.third_party_pct}%</td>
                      <td className="p-1 text-right text-[var(--text-primary)]">{b.price_diff}</td>
                      <td className={`p-1 ${b.stock_status === 'Low Stock' ? 'text-[var(--warning)]' : 'text-[var(--success)]'}`}>{b.stock_status}</td>
                      <td className="p-1">{b.alert ? <><Warning size={10} className="text-[var(--error)] inline" weight="fill" /> <span className="text-[var(--error)]">Auto-Triggered</span></> : <CheckCircle size={10} className="text-[var(--success)] inline" weight="fill" />}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
